import { useMemo, useState } from "react";
import { useTranslate } from "ra-core";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { ActivityLog } from "../activity/ActivityLog";
import {
  CONTACT_CREATED,
  CONTACT_NOTE_CREATED,
  DEAL_CREATED,
  DEAL_NOTE_CREATED,
  TASK_UPDATED,
} from "../consts";
import { useIsAdmin } from "../sales/useIsAdmin";
import { useDashboardScope } from "./useDashboardScope";

/** Short enough that the feed stays a footnote rather than a second page. */
const ACTIVITY_PAGE_SIZE = 5;

/**
 * Radix needs a value to address the single collapsible section by; it doubles
 * as the accordion's `defaultValue`, which is what makes the feed open on load.
 */
const ACCORDION_ITEM = "activity";

type ActivityFilterKey =
  | "all"
  | "team"
  | "notes"
  | "deals"
  | "contacts"
  | "tasks";

/**
 * What each chip narrows the feed to. `all` and `team` differ on WHOSE activity
 * is shown — every other chip keeps the dashboard scope and narrows on the kind
 * of activity instead. `null` means "every type".
 */
const FILTER_TYPES: Record<ActivityFilterKey, string[] | null> = {
  all: null,
  team: null,
  notes: [CONTACT_NOTE_CREATED, DEAL_NOTE_CREATED],
  deals: [DEAL_CREATED, DEAL_NOTE_CREATED],
  contacts: [CONTACT_CREATED],
  // Deliberately not folded into `notes`, `deals` or `contacts`: those narrow
  // on a kind of record, and a task update is none of the three.
  tasks: [TASK_UPDATED],
};

const FILTER_LABELS: Record<ActivityFilterKey, string> = {
  all: "crm.dashboard.activity.filter_all",
  team: "crm.dashboard.activity.filter_team",
  notes: "crm.dashboard.activity.filter_notes",
  deals: "crm.dashboard.activity.filter_deals",
  contacts: "crm.dashboard.activity.filter_contacts",
  tasks: "crm.dashboard.activity.filter_tasks",
};

const FILTER_KEYS = Object.keys(FILTER_LABELS) as ActivityFilterKey[];

/**
 * The activity feed, open under a one-line summary.
 *
 * It sits below everything else — it is history, not work, so it never opens
 * the dashboard — but it is unfolded on arrival: the feed is what most readers
 * scroll down for, and hiding it behind a click made it easy to miss. The
 * section stays collapsible for anyone who wants the room back.
 */
export function DashboardActivityLog() {
  const translate = useTranslate();
  const { salesId, isTeam } = useDashboardScope();
  const { isAdmin } = useIsAdmin();
  const [selected, setSelected] = useState<ActivityFilterKey>("all");

  /*
    "My team" drops the owner filter, so it is an administrator's chip. Offering
    it to everyone would let any account manager read the whole company's
    activity with one click, which no other surface allows.
  */
  const filterKeys = useMemo(
    () => (isAdmin ? FILTER_KEYS : FILTER_KEYS.filter((key) => key !== "team")),
    [isAdmin],
  );

  /*
    Memoised because a new object here is a new filter as far as the list is
    concerned: an inline literal gets a fresh identity on every render, so the
    feed refetched whenever anything above it re-rendered -- including when
    admin status resolves a tick after mount.
  */
  const filter = useMemo(() => {
    const types = FILTER_TYPES[selected];
    const isTeamWide = (selected === "team" && isAdmin) || isTeam;
    return {
      ...(isTeamWide ? {} : { sales_id: salesId }),
      ...(types ? { "type@in": `(${types.join(",")})` } : {}),
    };
  }, [selected, isAdmin, isTeam, salesId]);

  return (
    <Accordion type="single" collapsible defaultValue={ACCORDION_ITEM}>
      <AccordionItem value={ACCORDION_ITEM} className="border-t border-b-0">
        <AccordionTrigger className="text-muted-foreground">
          {translate("crm.dashboard.activity.summary")}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-4">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            spacing={2}
            value={selected}
            // Radix hands back "" when the pressed chip is the active one;
            // a feed with no chip selected would have no meaning, so the
            // current one simply stays on.
            onValueChange={(value) =>
              value ? setSelected(value as ActivityFilterKey) : undefined
            }
            className="flex-wrap"
          >
            {filterKeys.map((key) => (
              <ToggleGroupItem key={key} value={key} className="px-3">
                {translate(FILTER_LABELS[key])}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <ActivityLog pageSize={ACTIVITY_PAGE_SIZE} filter={filter} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
